import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  CalendarPlus,
  Megaphone,
  Receipt,
  ScanLine,
} from "lucide-react";
import type { EventStatus, EventSummary } from "@ticketnjoy/types";

import { QueryError } from "@/components/query-error";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useEventsQuery } from "@/features/events/api";
import { useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

const UPCOMING_LIMIT = 5;
const UPCOMING_STATUSES: readonly EventStatus[] = ["Draft", "Published", "Paused"];

const STATUS_VARIANTS: Partial<Record<EventStatus, "success" | "warning" | "outline">> = {
  Draft: "outline",
  Published: "success",
  Paused: "warning",
};

function pickUpcoming(events: EventSummary[], now: number): EventSummary[] {
  return events
    .filter((e) => UPCOMING_STATUSES.includes(e.status) && new Date(e.endsAt).getTime() >= now)
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())
    .slice(0, UPCOMING_LIMIT);
}

export function EditorHome({ companyName }: { companyName: string }) {
  const t = useT("home");
  const tStatus = useT("eventStatus");
  const fmt = useFormat();
  const events = useEventsQuery();
  const [now] = useState(() => Date.now());
  const upcoming = events.data ? pickUpcoming(events.data, now) : [];

  const quickLinks = [
    { to: "/events/new", icon: CalendarPlus, label: t("quick.newEvent") },
    { to: "/events", icon: CalendarDays, label: t("quick.events") },
    { to: "/promotions", icon: Megaphone, label: t("quick.promotions") },
    { to: "/orders", icon: Receipt, label: t("quick.orders") },
    { to: "/check-in", icon: ScanLine, label: t("quick.checkIn") },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("title")}</h1>
        <p className="text-sm text-muted-foreground">{companyName}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("quick.title")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {quickLinks.map(({ to, icon: Icon, label }) => (
            <Button key={to} variant="outline" className="h-auto justify-start gap-2 py-3" asChild>
              <Link to={to}>
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </Link>
            </Button>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">{t("upcoming.title")}</CardTitle>
            <CardDescription>{t("upcoming.description")}</CardDescription>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/events">{t("upcoming.viewAll")}</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {events.isError ? (
            <QueryError
              message={t("upcoming.error")}
              onRetry={() => events.refetch()}
              retrying={events.isFetching}
            />
          ) : events.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-14 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : upcoming.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("upcoming.empty")}</p>
          ) : (
            <ul className="divide-y">
              {upcoming.map((e) => (
                <li key={e.id}>
                  <Link
                    to={`/events/${e.slug}`}
                    className="flex items-center gap-3 py-3 transition hover:bg-muted/50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{e.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {fmt.dateTime(e.startsAt)}
                        {e.venueCity ? ` • ${e.venueCity}` : ""}
                      </p>
                    </div>
                    <span className="hidden text-xs text-muted-foreground tabular-nums sm:inline">
                      {t("upcoming.sold", {
                        sold: fmt.number(e.totalSold),
                        capacity: fmt.number(e.totalCapacity),
                      })}
                    </span>
                    <Badge variant={STATUS_VARIANTS[e.status] ?? "outline"}>
                      {tStatus(e.status)}
                    </Badge>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
